import React from "react";
import { useSearchParams } from "react-router-dom";
import { Wallet, Users, FileText, Calculator, BarChart2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "src/components/ui/tabs";
import type { CommissionViewType } from "./types/commissions.types";
import PayoutsView from "./views/payouts-view";
import ReferralsView from "./views/referrals-view";
import InvoicesView from "./views/invoices-view";
import GrossMarginView from "./views/gross-margin-view";
import ReportsView from "./views/reports-view";

const COMMISSION_VIEWS: { id: CommissionViewType; label: string; icon: React.ElementType }[] = [
  { id: "payouts", label: "Commissions", icon: Wallet },
  { id: "referrals", label: "Referrals", icon: Users },
  { id: "invoices", label: "Paid Invoices", icon: FileText },
  { id: "gross-margin", label: "Gross margin", icon: Calculator },
  { id: "reports", label: "Reports", icon: BarChart2 },
];

const VALID_VIEW_IDS = COMMISSION_VIEWS.map((v) => v.id);
const DEFAULT_VIEW: CommissionViewType = "payouts";

const CommissionsTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view") ?? DEFAULT_VIEW;
  const currentView: CommissionViewType = VALID_VIEW_IDS.includes(viewParam as CommissionViewType)
    ? (viewParam as CommissionViewType)
    : DEFAULT_VIEW;

  const setView = (viewId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("view", viewId);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full">
      <Tabs
        value={currentView}
        onValueChange={setView}
        className="flex flex-col h-full"
      >
        <TabsList className="w-full justify-start h-auto rounded-none border-b border-gray-200 bg-transparent p-0 gap-1">
          {COMMISSION_VIEWS.map(({ id, label, icon: Icon }) => (
            <TabsTrigger
              key={id}
              value={id}
              className={`gap-2 rounded-t-md rounded-b-none border-b-2 border-transparent px-4 py-3 -mb-px ${
                currentView === id
                  ? "border-blue-500 bg-white text-gray-900 shadow-none"
                  : "bg-gray-201 text-gray-700 hover:bg-gray-200 border-transparent"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex-1 min-h-0 overflow-auto mt-4">
          <TabsContent
            value="payouts"
            className="mt-0 h-full"
          >
            <PayoutsView />
          </TabsContent>
          <TabsContent
            value="referrals"
            className="mt-0 h-full"
          >
            <ReferralsView />
          </TabsContent>
          <TabsContent
            value="invoices"
            className="mt-0 h-full"
          >
            <InvoicesView />
          </TabsContent>
          <TabsContent
            value="gross-margin"
            className="mt-0 h-full"
          >
            <GrossMarginView />
          </TabsContent>
          <TabsContent
            value="reports"
            className="mt-0 h-full"
          >
            <ReportsView />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default CommissionsTab;
